import { useCallback, useEffect, useRef } from 'preact/hooks'
import { useSearchspringContext } from 'libs/searchspring/fetchers/provider'
import { SearchspringSuggestProvider } from 'libs/searchspring/fetchers/suggest'
import { SearchspringTrendingProvider } from 'libs/searchspring/fetchers/trending'
import { SearchPageLink } from 'libs/searchspring/components/quick-search/SearchPageLink'
import { QuickSearchSuggestions } from 'libs/searchspring/components/quick-search/QuickSearchSuggestions'
import { QuickSearchResults } from 'libs/searchspring/components/quick-search/QuickSearchResults'
import clsx from 'clsx'
import { globalState } from '@/components/helpers/utils'
import { withRecommendationProvider } from 'libs/searchspring/components/recommendations/withRecommendationsProvider'
import { GridResults } from 'libs/searchspring/components/recommendations/GridResults'
import { useSearchResultState } from '../../hooks/useSearchResultState'

/**
 * Gets popular categories HTML content from the DOM
 * @returns {string|undefined} HTML content for popular categories or undefined if not found
 */
const getPopularCategories = () => {
  const popularCategoryWrapper = document.getElementById('popular-categories')
  return popularCategoryWrapper?.innerHTML || undefined
}

// Extract and filter default suggestions from global state
const defaultSuggestions = globalState.search?.suggestions || []
const collectionsSuggestions = defaultSuggestions.filter(s => s.type === 'collections') || []
const pagesSuggestions = defaultSuggestions.filter(s => s.type === 'pages') || []

// Configuration for different search states
export const SEARCH_CONFIG = {
  NO_SEARCH: {
    suggestions: [
      { title: 'Popular Searches', type: 'collections', default: collectionsSuggestions },
      { title: 'Popular Pages', type: 'pages', default: pagesSuggestions, isStatic: true }
    ],
    mainTitle: 'Popular Categories'
  },
  NO_RESULTS: {
    suggestions: [
      { title: 'Popular Searches', type: 'collections', default: collectionsSuggestions },
      { title: 'Popular Pages', type: 'pages', default: pagesSuggestions, isStatic: true }
    ],
    mainTitle: 'Popular Products'
  },
  HAS_RESULTS: {
    suggestions: [
      { title: 'Suggested Searches', type: 'collections', default: collectionsSuggestions },
      { title: 'Popular Pages', type: 'pages', default: pagesSuggestions, isStatic: true }
    ],
    mainTitle: 'Search Results'
  },
  REDIRECT: {
    mainTitle: 'Suggested Pages'
  }
}

/**
 * QuickSearchDropdown component - Displays search results and suggestions based on query
 * @param {Object} props - Component props
 * @param {string} props.query - Search query string
 * @returns {JSX.Element} Rendered component
 */
export function QuickSearchDropdown({ query, redirect, setRedirect }) {
  const popularCategories = useRef(getPopularCategories())
  const { refine, results, isLoading } = useSearchspringContext()
  const { heading, hasResults, showNoResults, isCheckingResults } = useSearchResultState({ query, results, isLoading })
  const Recommendations = useCallback(withRecommendationProvider(GridResults, 'recommend'), [])

  const itemClass = 'flex flex-col items-stretch lg:basis-1/3 [&:nth-child(4)]:lg:hidden [&:nth-child(4)]:xl:flex lg:w-1/3 xl:basis-1/4 xl:w-1/4 basis-1/2 w-1/2 border border-neutral-medium shrink-0'
  const productCardOptions = {
    hideQuickAdd: true,
    hideSwatches: false,
    hidePromolines: false,
    hideBadges: true
  }

  // Update search query when query prop changes
  useEffect(() => {
    refine('query', query)
  }, [query])

  useEffect(() => {
    if (results?.merchandising?.redirect) {
      setRedirect(results?.merchandising?.redirect)
    } else {
      setRedirect(false)
    }
  }, [results?.merchandising?.redirect])

  /**
   * Renders suggestion components based on current search state
   * @returns {JSX.Element[]} Array of SearchSuggestions components
   */
  const renderSuggestions = () => {
    const config = query ? SEARCH_CONFIG.HAS_RESULTS : SEARCH_CONFIG.NO_RESULTS
    return config.suggestions.map((suggestion, index) => (
      <SearchSuggestions
        key={index}
        heading={suggestion.title}
        type={suggestion.type}
        query={query}
        defaultSuggestions={suggestion.default}
        isStatic={suggestion.isStatic}
      />
    ))
  }

  return (
    <div className={clsx(
      'grid grid-cols-1 h-full lg:grid-cols-[255px_auto] max-h-full lg:overflow-hidden transition-transform mt-12 -translate-y-4 group-data-visible/search:translate-y-0',
      'quick-search-dropdown-grid',
      hasResults
        ? 'quick-search-dropdown-grid--has-results'
        : 'quick-search-dropdown-grid--no-results',
      !query && 'quick-search-dropdown-grid--no-query'
    )}>
        <div
          className={clsx(
            'overflow-hidden h-max transition-all duration-300 text-center w-full bg-brand-yellow [grid-area:notice] max-h-0',
            (!hasResults && query && !isLoading && showNoResults) && 'max-h-20'
          )}
        >
          <span className="block px-6 py-2">No results for "{query}", please try another search</span>
        </div>

      {/* Loading Overlay */}
      {isCheckingResults && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-neutral-medium/40 backdrop-blur-xl">
          <div className="flex flex-col items-center gap-2">
            <div className="w-6 h-6 border-2 rounded-full border-brand-green border-t-transparent animate-spin"></div>
            <p className="text-sm text-neutral-secondary">Searching...</p>
          </div>
        </div>
      )}

      {/* Main Content Section */}
      <div className={clsx(
        '[grid-area:results] flex flex-col h-full lg-max:border-t border-neutral-medium',
        hasResults && 'border border-b-0 border-collapse lg-max:border-t-0'
      )}>
        <div className={clsx(
          'flex flex-col items-start justify-between w-full p-4 lg:p-6 lg:flex-row lg:items-center lg:border-l border-neutral-medium'
        )}>
          <p className="subtitle-lg lg:h7 shrink-0 whitespace-nowrap">{heading}</p>
        </div>

        <div className={clsx('flex grow', hasResults ? 'border border-b-0 border-collapse lg-max:border-t-0 ' : '')}>
          <div className={clsx('w-full flex grow', hasResults ? '' : 'hidden')}>
            <QuickSearchResults />
          </div>
          <div className={clsx('w-full flex grow', hasResults || !query ? 'hidden' : '')}>
            <Recommendations profile={'home-page'} limit={4} itemClass={itemClass} productCardOptions={productCardOptions} />
          </div>
          <div className={clsx('w-full flex grow', query ? 'hidden' : '')}>
            {popularCategories.current && <PopularCategories content={popularCategories.current} />}
          </div>
        </div>

        {(results?.pagination?.totalResults > 0) && (
          <SearchPageLink query={query} className="sticky bottom-0 uppercase lg-max:hidden btn btn-md btn-accent-2 shrink-0">
            View all {results?.pagination?.totalResults} results
          </SearchPageLink>
        )}
      </div>

      {/* Suggestions Section */}
      <div className={clsx(
        '[grid-area:suggestions] flex flex-col h-full divide-y divide-neutral-medium',
        hasResults && 'border-neutral-medium'
      )}>
        {renderSuggestions()}
      </div>

      {/* Mobile "View all results" button */}
      {(hasResults && !redirect) && (
        <SearchPageLink
          query={query}
          className="[grid-area:mobile-cta] sticky bottom-0 w-full h-fit uppercase btn btn-md btn-accent-2 shrink-0 lg:hidden"
        >
          View all {results?.pagination?.totalResults} results
        </SearchPageLink>
      )}
    </div>
  )
}

/**
 * PopularCategories component - Renders HTML content for popular categories
 * @param {Object} props - Component props
 * @param {string} props.content - HTML content to render
 * @returns {JSX.Element} Rendered component
 */
const PopularCategories = ({ content }) => {
  return (
    <div
      className="flex flex-row max-h-full gap-2 px-4 max-w-7xl lg:px-6 lg-max:overflow-x-auto lg:border-l border-neutral-medium"
      dangerouslySetInnerHTML={{ __html: content }}
    />
  )
}

/**
 * SearchSuggestions component - Renders search suggestions based on query
 * @param {Object} props - Component props
 * @param {string} props.query - Search query string
 * @param {string} props.type - Type of suggestions ('collections' or 'pages')
 * @param {string} props.heading - Heading text for the suggestions section
 * @param {Array} props.defaultSuggestions - Default suggestions to display
 * @returns {JSX.Element} Rendered component
 */
const SearchSuggestions = ({ query, type, heading, defaultSuggestions, isStatic = false }) => {
  return (
    <div className="flex flex-col gap-4 p-4 lg:p-6">
      <h3 className="subtitle-lg lg:h7">{heading}</h3>
      {isStatic ? (
        <QuickSearchSuggestions query={query} defaultSuggestions={defaultSuggestions} />
      ) : (
        query ? (
        <SearchspringSuggestProvider category={type}>
          <QuickSearchSuggestions query={query} defaultSuggestions={defaultSuggestions} />
        </SearchspringSuggestProvider>
        ) : (
        <SearchspringTrendingProvider category={type}>
          <QuickSearchSuggestions defaultSuggestions={defaultSuggestions} />
        </SearchspringTrendingProvider>
        ))}
    </div>
  )
}
